import React, { useState } from 'react';
import EPrescriptionWizardPatientDataForm, {
	EPrescriptionWizardPatientDataFormDefaultValues,
	EPrescriptionWizardPatientDataFormValues,
} from './EPrescriptionWizardPatientDataForm';
import { EPrescriptionWizardData } from '../../EPrescriptionWrapper';
import { useEffectAsync } from '../../../../../helpers';
import { useLazyPatientInformationQuery } from '../../../../../graphql/graphql';
import useLoading from '../../../../Base/Hooks/useLoading';
import { getMomentDate } from '../../../../../Utils/dates.util';
import useTranslation from '../../../../../Hooks/useTranslation';
import { SelectValue } from '../../../../../Components/Base/Select/Select';

interface Props {
	patientId: string;
	wizardData: EPrescriptionWizardData;
	onSubmit: (values: EPrescriptionWizardPatientDataFormValues) => void;
}

const EPrescriptionWizardPatientData: React.FC<Props> = ({
	patientId,
	wizardData,
	onSubmit,
}) => {
	const { t } = useTranslation();
	const { isLoading, setIsLoading, RenderLoading } = useLoading();
	const [defaultValues, setDefaultValues] = useState<
		EPrescriptionWizardPatientDataFormDefaultValues | undefined
	>();
	const [getPatientInformation] = useLazyPatientInformationQuery();

	const insuranceTypeDefaults: Record<string, SelectValue> = {
		['Veřejné zdravotní pojištění']: {
			label: t('terms.suklDruhPojisteni.verejne'),
			value: 'VEREJNE',
		},
		['1 - veřejné zdravotní pojištění (nepřeúčtované přes CMU)']: {
			label: t('terms.suklDruhPojisteni.verejne'),
			value: 'VEREJNE',
		},
		['Smluvní pojištění']: {
			label: t('terms.suklDruhPojisteni.smluvni'),
			value: 'SMLUVNI_PRIPOJISTENI',
		},
		['Cestovní pojištění']: {
			label: t('terms.suklDruhPojisteni.cestovni'),
			value: 'CESTOVNI_PRIPOJISTENI',
		},
		['Mezistátní pojištění (včetně EU)']: {
			label: t('terms.suklDruhPojisteni.pojisteniEu'),
			value: 'POJISTENI_EU',
		},
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		if (wizardData.patientInformation) {
			//IF HAVE DATA FROM WIZARD USE AS DEFAULT VALUES
			setDefaultValues(wizardData.patientInformation);
		} else {
			//IF DONT HAVE DATA FROM WIZARD LOAD DEFAULTS FROM API
			const { user } = await getPatientInformation({
				userId: patientId,
			}).unwrap();

			if (user && user.profile) {
				const {
					firstName,
					lastName,
					birthDate,
					address,
					insuranceNumber,
					insuranceCompanyTermItem,
					gender,
				} = user.profile;

				const newDefaultValues: EPrescriptionWizardPatientDataFormDefaultValues =
					{
						firstName,
						lastName,
						dateOfBirth: birthDate ? getMomentDate(birthDate) : undefined,
						phoneNumber: user?.formattedPhoneNumber,
						electronicMail: user?.email,
						insuranceNumber: insuranceNumber ? insuranceNumber : undefined,
						insuranceType:
							insuranceTypeDefaults[
								user?.vzpResult?.result?.prubehPojisteni?.druhPojisteni
							],
						insuranceCompany:
							insuranceCompanyTermItem && insuranceCompanyTermItem.value
								? {
										label: `${insuranceCompanyTermItem.value} - ${insuranceCompanyTermItem.value2}`,
										value: insuranceCompanyTermItem.value,
								  }
								: undefined,
						gender,
						...(address && {
							street: address.street,
							descriptiveNumber: address.descriptiveNumber,
							guideNumber: address.guideNumber
								? parseInt(address.guideNumber)
								: undefined,
							city: address.city,
							postCode: address.postCode,
						}),
						...(user?.healthCard && {
							weight: user?.healthCard.weight,
						}),
					};
				setDefaultValues(newDefaultValues);
			}
		}
		setIsLoading(false);
	}, []);

	if (isLoading) return <RenderLoading />;

	return (
		<EPrescriptionWizardPatientDataForm
			onSubmit={onSubmit}
			defaultValues={defaultValues}
		/>
	);
};

export default EPrescriptionWizardPatientData;
